// Entra ID Protection "unfamiliar sign-in properties" — the pipeline-owned
// `unfamiliar_properties_json` on a pass-through alert (ALERT-FACTS-CONTRACT.md §10,
// schema_version 1). Pure logic, no React: AlertsPage and TestRunResultsPage render it through
// the one shared UnfamiliarPropertiesPanel (CLAUDE.md rule #13).
//
// The pipeline does the comparison. Every verdict is taken FROM the same alert's Baseline
// Comparison rows, so the two panels cannot disagree, and the portal must not re-derive one.
// This module only shapes what is stored for display.

export const SUPPORTED_SCHEMA_VERSION = 1;

const VERDICTS = new Set(['match', 'partial', 'deviation', 'no_data']);

// What each `basis` means for the reader. A verdict is only as good as what it compared, and
// three of these compare something narrower than Microsoft's own check — say so on the row.
export const BASIS_NOTES = Object.freeze({
  provider: 'Compared by network provider name. There is no AS-number baseline.',
  user_ip_subnet: "Compared with this user's own IP addresses. Microsoft's check is tenant-wide, and there is no tenant subnet baseline.",
  os_only: 'Only the operating system is compared. Device name and ID have no baseline.',
});

/**
 * Parse the stored property. Returns:
 *   null                                  — absent or unreadable (render nothing)
 *   { unsupported: true, schemaVersion }  — a version this portal does not know (say so)
 *   { schemaVersion, source, riskEventType, signinMatched, properties: [...] , counts }
 */
export function parseUnfamiliarProperties(json) {
  if (json == null || json === '') return null;
  let o = json;
  if (typeof json === 'string') {
    try { o = JSON.parse(json); } catch { return null; }
  }
  if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
  if (o.schema_version !== SUPPORTED_SCHEMA_VERSION) {
    return { unsupported: true, schemaVersion: o.schema_version ?? null };
  }
  const raw = Array.isArray(o.properties) ? o.properties : [];
  const properties = raw
    .filter((p) => p && typeof p === 'object')
    .map((p, i) => ({
      key: `${p.reason || p.property || 'p'}-${i}`,
      property: p.property || p.reason || 'Unknown',
      reason: p.reason || null,
      value: p.value ?? null,
      // Only an explicit `true` is a recorded value: an absent flag must not read as one.
      recorded: p.recorded === true,
      valueSource: p.value_source ?? null,
      baseline7d: p.baseline_7d ?? null,
      baseline30d: p.baseline_30d ?? null,
      // An unknown verdict reads as no comparison, never as a match.
      verdict: VERDICTS.has(p.verdict) ? p.verdict : 'no_data',
      basis: p.basis ?? null,
      basisNote: BASIS_NOTES[p.basis] || null,
    }));
  return {
    schemaVersion: o.schema_version,
    source: o.source || null,
    riskEventType: o.risk_event_type || null,
    signinMatched: o.signin_matched === true,
    properties,
    counts: countVerdicts(properties),
  };
}

export function countVerdicts(properties) {
  const c = { deviation: 0, partial: 0, match: 0, no_data: 0 };
  for (const p of properties || []) c[p.verdict] = (c[p.verdict] || 0) + 1;
  return c;
}

/** Panel heading: "Unfamiliar Sign-in Properties · 7 flagged · 3 deviations · 1 partial". */
export function headingText(parsed) {
  const parts = ['Unfamiliar Sign-in Properties'];
  if (!parsed || parsed.unsupported) return parts[0];
  const n = parsed.properties.length;
  parts.push(`${n} flagged by Microsoft`);
  const { deviation, partial } = parsed.counts;
  if (deviation) parts.push(`${deviation} deviation${deviation !== 1 ? 's' : ''}`);
  if (partial) parts.push(`${partial} partial`);
  return parts.join('  ·  ');
}

/**
 * Text for a property's value cell when there is no value. Two different absences:
 * the matched sign-in did not record it, or no sign-in matched at all, so nothing
 * could come from one. Reading the second as the first would claim we looked.
 */
export function missingValueText(parsed) {
  return parsed && parsed.signinMatched ? 'not recorded on the sign-in' : 'not available (no sign-in matched)';
}

/** Footer lines describing where the values came from. */
export function provenanceNotes(parsed) {
  if (!parsed || parsed.unsupported) return [];
  const notes = [];
  notes.push(parsed.source === 'description'
    ? "The flagged properties were read from the alert description, because Microsoft's risk detection was not in our logs yet."
    : "The flagged properties are Microsoft's, in Microsoft's order, from the risk detection behind this alert.");
  if (!parsed.signinMatched) {
    notes.push("No sign-in in our logs matched this alert's request ID, so the values come from the alert's own IP address and its enrichment.");
  }
  notes.push('Each verdict is taken from this alert\'s Baseline Comparison, compared by the detection pipeline at alert time.');
  return notes;
}
